import React from 'react';
import { Skeleton, Typography } from 'antd';
import { gql, useQuery } from '@apollo/client';

import { Button } from 'components/AntD';

const FETCH_CANDIDATE_PUBLIC_PROFILE_COUNTERS = gql`
  query fetchCandidatePublicProfileCounters($input: FetchCandidatePublicProfileInput!) {
    fetchCandidatePublicProfileCounters(input: $input) {
      candidateProfileId
      numQuestions
      numWordsWritten
      numReferencesCompleted
    }
  }
`;

interface Props {
  candidateFirstName?: string;
  ctaCallback?(): void;
  vanityLinkText: string;
}

function CandidateSummaryCard({
  candidateFirstName,
  ctaCallback,
  vanityLinkText,
}: Props): JSX.Element {
  const { data, loading } = useQuery(FETCH_CANDIDATE_PUBLIC_PROFILE_COUNTERS, {
    variables: {
      input: {
        code: vanityLinkText,
      },
    },
    skip: !vanityLinkText,
  });

  const numWordsWritten = data?.fetchCandidatePublicProfileCounters?.numWordsWritten;
  const numQuestions = data?.fetchCandidatePublicProfileCounters?.numQuestions;
  const numReferencesCompleted = data?.fetchCandidatePublicProfileCounters?.numReferencesCompleted;

  return (
    <div className="tw-flex tw-flex-col tw-gap-3 tw-border tw-rounded-sm tw-px-8 tw-py-5 tw-bg-white tw-border-gray-200">
      <Typography.Title level={4} className="tw-text-lg tw-fond-bold tw-text-gray-700">
        Are you interested in hiring {candidateFirstName}? Read the full report.
      </Typography.Title>
      <span className="tw-text-sm">
        {candidateFirstName}&rsquo;s full report contains answers for strengths, areas for
        development, and advice for hiring managers.
      </span>
      <br />
      <div className="tw-px-reg tw-flex tw-flex-col sm:tw-flex-row tw-justify-between tw-flex-nowrap">
        <div className="tw-text-center">
          <Typography.Title
            level={4}
            className="tw-text-center tw-font-extrabold tw-text-3xl tw-leading-10"
          >
            {loading ? <Skeleton.Button /> : <>{numWordsWritten || 0}</>}
          </Typography.Title>
          <span className="tw-text-center tw-text-sm">Words Written</span>
        </div>
        <div className="tw-text-center">
          <Typography.Title
            level={4}
            className="tw-text-center tw-font-extrabold tw-text-3xl tw-leading-10"
          >
            {loading ? <Skeleton.Button /> : <>{numQuestions || 0}</>}
          </Typography.Title>
          <span className="tw-text-center tw-text-sm">
            Question{numQuestions === 1 ? '' : 's'} Answered
          </span>
        </div>
        <div className="tw-text-center">
          <Typography.Title
            level={4}
            className="tw-text-center tw-font-extrabold tw-text-3xl tw-leading-10"
          >
            {loading ? <Skeleton.Button /> : <>{numReferencesCompleted || 0}</>}
          </Typography.Title>
          <span className="tw-text-center tw-text-sm">
            Detailed Reference{numQuestions === 1 ? '' : 's'}
          </span>
        </div>
      </div>
      <div>
        <div className="tw-text-center tw-mt-3">
          <Button variant="contained" color="primary" size="middle" onClick={ctaCallback}>
            View Full Report
          </Button>
        </div>
      </div>
    </div>
  );
}

export default CandidateSummaryCard;
